import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../Modal";
import { useModal } from "../../contexts/modal";
import service from "../../service";
import { DateTime } from 'luxon';
import MessageTemplateForm from '../MessageTemplateForm';
import { toast } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import {
  AddFilterTrigger,
  FilterBar,
  FilterEditor,
  useFilters,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import {
  DataTable,
  DataTablePagination,
  defineTableSchema,
  useConfiguredTable,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Checkbox } from "@art-of-hospitality-solutions-llc/ui-kit/components/checkbox";
import { Button } from "@art-of-hospitality-solutions-llc/ui-kit/components/button";
import ShowFor from '@Components/ShowFor';

const FALLBACK_DATA = [];

export const DEFAULT_MESSAGE_TEMPLATE_COLUMN_CONFIG = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected() ? true : row.getIsSomeSelected?.() ? 'indeterminate' : false}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    type: "boolean",
    size: 50
  },
  {
    id: 'name',
    headerLabel: 'Title',
    accessor: (row) => row?.name ?? 'N/A',
    type: 'text',
    size: 350,
  },
  {
    id: 'method',
    headerLabel: 'Channel',
    accessor: (row) => row.method ?? 'N/A',
    type: 'text',
    size: 100,
  },
  {
    id: 'category',
    headerLabel: 'Category',
    accessor: (row) => row.category ?? '',
    type: 'text',
    size: 160,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) => row.createdAt ?? 'N/A',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
  {
    id: 'createdBy',
    headerLabel: 'Created By',
    accessor: (row) => {
      const name = row?.createdByUser?.name;
      return name ?? 'N/A'
    },
    type: 'text',
  },
];

const TEMPLATE_COLUMN_CONFIG = DEFAULT_MESSAGE_TEMPLATE_COLUMN_CONFIG.filter(
  (column) => column.id !== 'category',
);

const MessageTemplateList = ({ messageTemplates, modalContent, setModalContent, refetch, isQuickMessage = false }) => {
  const [targetTemplate, setTargetTemplate] = useState(null);
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();


  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnPinning, setColumnPinning] = useState({
    left: ['select', 'name'],
    right: [],
  });
  const { filters, addOrUpdateFilter, removeFilter } = useFilters([]);
  let sortBy = 'createdAt';

  const columnConfigs = isQuickMessage
    ? DEFAULT_MESSAGE_TEMPLATE_COLUMN_CONFIG
    : TEMPLATE_COLUMN_CONFIG;

  const { columns, filters: filterDefinitions } =
    defineTableSchema(columnConfigs);

  const { table } = useConfiguredTable({
    data: messageTemplates ?? FALLBACK_DATA,
    columns,
    filters: filters,
    initialSorting: [{ id: sortBy, desc: true }],
    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    columnPinning,
    setColumnPinning,
    autoResetPageOnFilterChange: true,
  });

  const handleShowEditor = async () => {
    const selectedRow = table.getSelectedRowModel().rows.map((row) => row?.original)
    if (selectedRow.length !== 1) return
    const template = selectedRow[0];

    setModalContent('edit');
    setTargetTemplate(template);
    openModal();
  };

  const handleSubmitEdits = async (data) => {
    if (data.html && !data.messageBody) data.messageBody = 'html';
    try {
      const res = await service.updateMessageTemplate(targetTemplate.id, data)

      if (res.ok) {
        table.resetRowSelection();
        closeModal();
        refetch();
      }
      return res;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  const handleDeleteSelected = async () => {
    const selectedRows = table.getSelectedRowModel().rows.map((row) => row?.original?.id)

    if (selectedRows.length > 0) {
      setModalContent('delete');
      setSelectedTemplateIds(selectedRows);
      openModal();
    }
  };

  const confirmDelete = async () => {
    for (const id of selectedTemplateIds) {
      await service.deleteMessageTemplate(id);
    }

    refetch();
    table.resetRowSelection();
    setSelectedTemplateIds([]);
    closeModal();
    toast('Message Template/s Deleted')
  };

  return (
    <div className="">
      <div className="flex w-full mb-4 gap-4">
        <ShowFor permissions={['MESSAGE_TEMPLATES_WRITE']}>
          <Button
            onClick={handleShowEditor}
            disabled={table?.getSelectedRowModel()?.rows?.length !== 1}
            className={`${table?.getSelectedRowModel()?.rows?.length !== 1
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : ""
              } px-4 py-2`}
          >
            Edit
          </Button>

          <Button
            onClick={handleDeleteSelected}
            disabled={!table.getSelectedRowModel().rows.length}
            className={`${!table.getSelectedRowModel().rows.length
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gray-300 hover:bg-red-500 hover:text-white active:bg-red-600 active:text-white"
              } px-4 py-2`}
          >
            Delete
          </Button>
        </ShowFor>
      </div>

      <div className="overflow-x-auto space-y-4">
        <FilterBar>
          {filters.map((f) => {
            const def = filterDefinitions.find((d) => d.field === f.field);
            if (!def) return null;
            return (
              <FilterEditor
                key={f.field}
                field={f.field}
                displayName={f.displayName}
                filterItem={f}
                inputType={def.type}
                operators={def.operators}
                applyFilter={addOrUpdateFilter}
                removeFilter={removeFilter}
                description={def.description}
              />
            );
          })}

          <AddFilterTrigger
            availableFilters={filterDefinitions.filter(
              (def) => !filters.some((f) => f.field === def.field),
            )}
            onAdd={(newFilter) => addOrUpdateFilter(newFilter)}
          />
        </FilterBar>

        <div className="flex items-center gap-4">
          <Input
            placeholder="Filter by template title..."
            value={
              filters.find((f) => f.field === 'name')?.value.toString() ?? ''
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                addOrUpdateFilter({
                  field: 'name',
                  operator: 'contains',
                  value: value.toString(),
                  displayName: 'Channel',
                  displayValue: value,
                });
              } else {
                removeFilter('name');
              }
            }}
            className="w-64"
          />

          <Input
            placeholder="Filter by communication channel..."
            value={
              filters.find((f) => f.field === 'method')?.value.toString() ?? ''
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                addOrUpdateFilter({
                  field: 'method',
                  operator: 'contains',
                  value: value.toString(),
                  displayName: 'Channel',
                  displayValue: value,
                });
              } else {
                removeFilter('method');
              }
            }}
            className="w-64"
          />
        </div>

        <DataTable
          table={table}
          onRowClick={(row) => {
            navigate(`/message-templates/${row.original.id}`)
            // handleView(row?.original);
          }}
          isLoading={status === 'pending'}
          skeletonRowCount={pageSize}
        />
        <DataTablePagination
          table={table}
          meta={{ total: table.getFilteredRowModel()?.rows?.length }}
          pagination={{ size: pageSize, number: pageIndex + 1 }}
          setPage={({ size, number }) => {
            setPageSize(size);
            setPageIndex(number - 1);
          }}
          setPageNumber={(n) => setPageIndex(n - 1)}
        />

      </div>
      <Modal className={"max-w-[80%]"}>
        {modalContent === 'edit' && targetTemplate && (
          <>
            <h2 className="mb-4 text-lg font-bold">Edit Template</h2>
            <MessageTemplateForm
              defaultValues={targetTemplate}
              onSubmitHandler={handleSubmitEdits}
              isEdit
            />
          </>
        )}

        {modalContent === 'delete' && (
          <div className="text-center">
            <p className="mb-4 text-lg">
              Are you sure you want to delete this message?
            </p>
            <div className="flex justify-center space-x-4">
              <Button
                onClick={confirmDelete}
                className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
              >
                Confirm
              </Button>
              <Button
                onClick={closeModal}
                className="rounded bg-gray-300 px-4 py-2 text-gray-800 hover:bg-gray-400"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MessageTemplateList;
